import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { mbcbService, MBCBReport, ReportFilters, ReportStats } from '../api/mbcbService';
import { planterService } from '../api/planterService';

const EMPTY_REPORTS: MBCBReport[] = [];

type SundayKey = 's1' | 's2' | 's3' | 's4' | 's5';
const SUNDAYS: SundayKey[] = ['s1', 's2', 's3', 's4', 's5'];

const fmtNum = (v: unknown) => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? String(n) : '—';
};

const fmtMoney = (v: unknown) => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

function parseSundayRow(
  weekly: Record<string, unknown> | null | undefined,
  rowKey: string,
): Record<SundayKey, number> {
  const empty: Record<SundayKey, number> = { s1: 0, s2: 0, s3: 0, s4: 0, s5: 0 };
  if (!weekly) return empty;
  const row = weekly[rowKey];
  if (!row || typeof row !== 'object') return empty;
  const o = row as Record<string, unknown>;
  const out = { ...empty };
  SUNDAYS.forEach((sk) => {
    const n = o[sk];
    out[sk] = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  });
  return out;
}

const MBCBReports: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedReport, setSelectedReport] = useState<MBCBReport | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [bulkActionMessage, setBulkActionMessage] = useState<string | null>(null);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [requestMonth, setRequestMonth] = useState(new Date().getMonth() || 12);
  const [requestYear, setRequestYear] = useState(
    new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
  );
  const [requestPlanterId, setRequestPlanterId] = useState<number | ''>('');
  const [requestMessage, setRequestMessage] = useState<string | null>(null);
  const [showRemindUnsubmittedModal, setShowRemindUnsubmittedModal] = useState(false);
  const [remindMessage, setRemindMessage] = useState<string | null>(null);

  // Debounce the search input so we issue requests as the user pauses typing.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter or search change should return to the first page so results aren't
  // hidden on a page that no longer exists for the narrowed result set.
  useEffect(() => {
    setPage(1);
  }, [year, month, statusFilter, debouncedSearch]);

  const filters: ReportFilters = {
    page,
    limit: 20,
    year,
    month,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
  };

  // Fetch reports
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['mbcbReports', filters],
    queryFn: () => mbcbService.getAllReports(filters),
  });

  // Fetch pending reports
  const { data: pendingData } = useQuery({
    queryKey: ['mbcbReportsPending'],
    queryFn: () => mbcbService.getPendingReports(),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['mbcbStats', year, month],
    queryFn: () => mbcbService.getReportStats(year, month),
  });

  const { data: plantersData } = useQuery({
    queryKey: ['mbcbRequestPlanters'],
    queryFn: () => planterService.getAllPlanters({ limit: 500, order: 'ASC', sortBy: 'name' }),
  });

  // Fetch full detail for the report being viewed so the modal matches the hub planter page
  const { data: detailData } = useQuery({
    queryKey: ['mbcbReportDetail', selectedReport?.id],
    queryFn: () => mbcbService.getReport(selectedReport!.id),
    enabled: showDetailModal && !!selectedReport?.id,
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      mbcbService.approveReport(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReportsPending'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbStats'] });
      setShowApproveModal(false);
      setSelectedReport(null);
      setReviewNotes('');
    },
  });

  // Request revision mutation
  const revisionMutation = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) =>
      mbcbService.requestRevision(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReportsPending'] });
      setShowRevisionModal(false);
      setSelectedReport(null);
      setReviewNotes('');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => mbcbService.deleteReport(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReportsPending'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbStats'] });
      setShowDeleteModal(false);
      setShowDetailModal(false);
      setSelectedReport(null);
      setSelectedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => mbcbService.bulkDeleteReports(ids),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbReportsPending'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbStats'] });
      setShowBulkDeleteModal(false);
      setSelectedIds(new Set());
      const { deletedIds, notDeleted } = res.data;
      if (notDeleted.length > 0) {
        setBulkActionMessage(
          `Deleted ${deletedIds.length} report(s). ${notDeleted.length} could not be deleted (not found or not permitted).`,
        );
      } else {
        setBulkActionMessage(`Deleted ${deletedIds.length} report(s).`);
      }
    },
    onError: () => {
      setBulkActionMessage('Bulk delete failed. Try again or delete one at a time.');
    },
  });

  const remindUnsubmittedMutation = useMutation({
    mutationFn: () => mbcbService.remindUnsubmittedReports(),
    onMutate: () => {
      setRemindMessage(null);
    },
    onSuccess: (response) => {
      const result = response.data;
      const parts = [`Sent ${result.sentCount} reminder${result.sentCount === 1 ? '' : 's'}`];
      if (result.skippedCount > 0) {
        parts.push(`skipped ${result.skippedCount} (no linked account)`);
      }
      if (result.failedCount > 0) {
        parts.push(`${result.failedCount} failed`);
      }
      if (result.consideredCount === 0) {
        setRemindMessage('No unsubmitted drafts to remind about.');
      } else {
        setRemindMessage(`${parts.join(', ')}.`);
      }
      setShowRemindUnsubmittedModal(false);
    },
  });

  const requestReportsMutation = useMutation({
    mutationFn: () =>
      mbcbService.requestReports({
        reportMonth: requestMonth,
        reportYear: requestYear,
        planterId: requestPlanterId === '' ? undefined : requestPlanterId,
        plantingStepScope: 'funding',
      }),
    onMutate: () => {
      setRequestMessage(null);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['mbcbReports'] });
      queryClient.invalidateQueries({ queryKey: ['mbcbStats'] });
      const result = response.data;
      const scope = result.mode === 'single' ? 'selected planter' : 'eligible planters';
      setRequestMessage(
        `Requested ${requestMonth}/${requestYear} for ${scope}: ${result.createdCount} created, ${result.skippedCount} already existed.`,
      );
    },
  });

  const reports = reportsData?.data ?? EMPTY_REPORTS;
  const pagination = reportsData?.pagination;

  const pageReportIds = useMemo(() => reports.map((r: MBCBReport) => r.id), [reports]);
  const allPageSelected =
    pageReportIds.length > 0 && pageReportIds.every((id: number) => selectedIds.has(id));
  const somePageSelected =
    pageReportIds.some((id: number) => selectedIds.has(id)) && !allPageSelected;

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) {
      el.indeterminate = somePageSelected;
    }
  }, [somePageSelected, pageReportIds]);

  const toggleSelectId = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        pageReportIds.length > 0 && pageReportIds.every((id: number) => next.has(id));
      if (allSelected) {
        pageReportIds.forEach((id: number) => next.delete(id));
      } else {
        pageReportIds.forEach((id: number) => next.add(id));
      }
      return next;
    });
  };
  const pendingReports = pendingData?.data || [];
  const stats = statsData?.data as ReportStats | undefined;
  const hasStats = !!stats && Number(stats.reportCount ?? 0) > 0;
  const fmtInt = (v: unknown) => {
    if (v == null || v === '') return '—';
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? String(Math.round(n)) : '—';
  };
  const statsPeriodLabel = month
    ? new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
    : String(year);
  const planters = (plantersData?.data || []).filter(
    (planter: any) => (planter.effectivePlantingStep ?? planter.plantingStep) === 'funding',
  );

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-indigo-100 text-indigo-800',
      approved: 'bg-green-100 text-green-800',
      needs_revision: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  const handleDeleteClick = (report: MBCBReport) => {
    setSelectedReport(report);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedReport) {
      deleteMutation.mutate(selectedReport.id);
    }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">MBCB Reports</h1>
            <p className="mt-2 text-sm text-gray-600">
              View, approve, and manage monthly ministry reports from church planters
            </p>
          </div>
          <Link
            to="/reports/import"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
          >
            Import CSV
          </Link>
        </div>

        {/* Quick Stats */}
        {pendingReports.length > 0 && (
          <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {pendingReports.length} {pendingReports.length === 1 ? 'report' : 'reports'} pending review
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  These reports need your attention
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Approved report totals for the selected period */}
        {hasStats && (
          <div className="mb-8">
            <h2 className="text-sm font-medium text-gray-500 mb-3">
              Approved totals · {statsPeriodLabel}
            </h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {(
                [
                  ['Approved reports', fmtInt(stats?.reportCount)],
                  ['Avg worship', fmtInt(stats?.avgAttendance)],
                  ['Baptisms', fmtInt(stats?.totalBaptisms)],
                  ['Faith decisions', fmtInt(stats?.totalSalvations)],
                  ['Budget receipts', fmtMoney(stats?.totalIncome)],
                  ['Mission giving', fmtMoney(stats?.totalMissionsGiving)],
                ] as [string, string][]
              ).map(([label, value]) => (
                <div key={label} className="rounded-lg bg-white shadow px-4 py-3">
                  <dt className="text-xs text-gray-500">{label}</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Remind planters to submit</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Email each funding-stage planter who still has a draft or revision-needed report
                  about their most recent unsubmitted month. Post-funding, training, and already
                  submitted planters are skipped. This does not create new drafts.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRemindMessage(null);
                  setShowRemindUnsubmittedModal(true);
                }}
                disabled={remindUnsubmittedMutation.isPending}
                className="inline-flex shrink-0 justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {remindUnsubmittedMutation.isPending ? 'Sending…' : 'Remind all with unsubmitted drafts'}
              </button>
            </div>
            {remindMessage && (
              <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                {remindMessage}
              </div>
            )}
            {remindUnsubmittedMutation.isError && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                Failed to send reminders. Try again.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Request Report Drafts</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Create missing drafts for a specific month for funding-stage planters only
                  (not training or post-funding), or one funding-stage planter.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={requestMonth}
                  onChange={(e) => setRequestMonth(parseInt(e.target.value, 10))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={requestYear}
                  onChange={(e) => setRequestYear(parseInt(e.target.value, 10))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planter</label>
                <select
                  value={requestPlanterId}
                  onChange={(e) => setRequestPlanterId(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All funding-stage planters</option>
                  {planters.map((planter: any) => (
                    <option key={planter.id} value={planter.id}>
                      {planter.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
                <button
                  onClick={() => requestReportsMutation.mutate()}
                  disabled={requestReportsMutation.isPending}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {requestReportsMutation.isPending ? 'Requesting…' : 'Request Drafts'}
                </button>
              </div>
            </div>
            {requestMessage && (
              <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                {requestMessage}
              </div>
            )}
            {requestReportsMutation.isError && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                Failed to request drafts.
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4">
              <label htmlFor="report-search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  id="report-search"
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by planter, spouse, church, city, or state"
                  className="block w-full rounded-md border-gray-300 pl-10 pr-10 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={month || ''}
                  onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Months</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="needs_revision">Revision Requested</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
                <button
                  onClick={() => {
                    setYear(new Date().getFullYear());
                    setMonth(undefined);
                    setStatusFilter('');
                    setSearchInput('');
                  }}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {bulkActionMessage && (
            <div className="px-4 py-3 border-b border-gray-200 bg-slate-50 text-sm text-slate-800 flex flex-wrap items-center justify-between gap-2">
              <span>{bulkActionMessage}</span>
              <button
                type="button"
                onClick={() => setBulkActionMessage(null)}
                className="text-sm font-medium text-primary-600 hover:text-primary-800"
              >
                Dismiss
              </button>
            </div>
          )}
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {selectedIds.size > 0 ? (
                <span>
                  <span className="font-medium text-gray-900">{selectedIds.size}</span> selected
                  {pagination && pagination.pages > 1 ? ' (across pages)' : ''}
                </span>
              ) : (
                <span>Select rows to delete in bulk</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setShowBulkDeleteModal(true)}
              disabled={selectedIds.size === 0 || bulkDeleteMutation.isPending}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkDeleteMutation.isPending ? 'Deleting…' : `Delete selected (${selectedIds.size})`}
            </button>
          </div>
          <ResponsiveTableWrapper>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="pl-4 pr-2 py-3 text-left w-10 sm:pl-6">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allPageSelected && pageReportIds.length > 0}
                    onChange={toggleSelectAllOnPage}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    aria-label="Select all reports on this page"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Church Planter
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Church
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Period
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Submitted
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                    Loading...
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                    No reports found
                  </td>
                </tr>
              ) : (
                reports.map((report: MBCBReport) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="pl-4 pr-2 py-4 align-top sm:pl-6">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(report.id)}
                        onChange={() => toggleSelectId(report.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        aria-label={`Select report ${report.id}`}
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {report.planter?.name || 'Unknown'}
                      </div>
                      {report.planter?.spouseName && (
                        <div className="text-sm text-gray-500">& {report.planter.spouseName}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-normal text-sm text-gray-900 sm:px-6 sm:whitespace-nowrap">
                      {report.planter?.churchName || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 sm:px-6">
                      {new Date(report.reportYear, report.reportMonth - 1).toLocaleString('default', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                          report.status
                        )}`}
                      >
                        {report.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 sm:px-6">
                      {report.submittedAt
                        ? new Date(report.submittedAt).toLocaleDateString()
                        : 'Not submitted'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                      <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end sm:items-center">
                        <button
                          onClick={() => {
                            setSelectedReport(report);
                            setShowDetailModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View
                        </button>
                        {(report.status === 'submitted' || report.status === 'under_review') && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedReport(report);
                                setShowApproveModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                setSelectedReport(report);
                                setShowRevisionModal(true);
                              }}
                              className="text-yellow-600 hover:text-yellow-900"
                            >
                              Request Revision
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteClick(report)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </ResponsiveTableWrapper>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page}</span> of{' '}
                    <span className="font-medium">{pagination.pages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                      disabled={page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedReport && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowDetailModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                  {(() => {
                    const view: MBCBReport = (detailData?.data as MBCBReport) ?? selectedReport;
                    const income = view.givingBreakdown?.income;
                    const mission = view.givingBreakdown?.missionGiving;
                    const weekly = view.weeklyMetrics as Record<string, unknown> | undefined;
                    const hasWeekly =
                      !!weekly && typeof weekly === 'object' && 'worshipAttendance' in weekly;
                    return (
                      <>
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                          {new Date(view.reportYear, view.reportMonth - 1).toLocaleString('default', {
                            month: 'long',
                            year: 'numeric',
                          })}{' '}
                          report - {view.planter?.name}
                        </h3>
                        <div className="space-y-6 text-sm">
                          {/* Their month in ministry */}
                          <section className="rounded-lg border border-amber-100 bg-amber-50/50 p-4 space-y-4">
                            <h4 className="font-semibold text-gray-900">Their month in ministry</h4>
                            <div>
                              <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                                Celebrate
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-gray-900">
                                {view.ministryHighlights || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                                Pray with them about
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-gray-900">
                                {view.prayerRequests || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                                How the network can help
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-gray-900">
                                {view.supportNeeded || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                                Generosity &amp; mission (story)
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-gray-900">
                                {view.additionalNotes || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                                Hope for next month
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-gray-900">
                                {view.nextMonthFocus || '—'}
                              </p>
                            </div>
                          </section>

                          {/* At a glance */}
                          <section>
                            <h4 className="font-semibold text-gray-900 mb-3">At a glance</h4>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="rounded-md bg-gray-50 p-3">
                                <dt className="text-gray-500 text-xs">Worship attendance (month total)</dt>
                                <dd className="font-medium text-gray-900 mt-0.5">
                                  {fmtNum(view.sundayAttendance)}
                                </dd>
                              </div>
                              <div className="rounded-md bg-gray-50 p-3">
                                <dt className="text-gray-500 text-xs">New members</dt>
                                <dd className="font-medium text-gray-900 mt-0.5">{fmtNum(view.newMembers)}</dd>
                              </div>
                              <div className="rounded-md bg-gray-50 p-3">
                                <dt className="text-gray-500 text-xs">Baptisms</dt>
                                <dd className="font-medium text-gray-900 mt-0.5">{fmtNum(view.baptisms)}</dd>
                              </div>
                              <div className="rounded-md bg-gray-50 p-3">
                                <dt className="text-gray-500 text-xs">New faith decisions (professions)</dt>
                                <dd className="font-medium text-gray-900 mt-0.5">{fmtNum(view.salvations)}</dd>
                              </div>
                              <div className="rounded-md bg-gray-50 p-3">
                                <dt className="text-gray-500 text-xs">Total budget receipts</dt>
                                <dd className="font-medium text-gray-900 mt-0.5">{fmtMoney(view.monthlyIncome)}</dd>
                              </div>
                              <div className="rounded-md bg-gray-50 p-3">
                                <dt className="text-gray-500 text-xs">Total mission giving</dt>
                                <dd className="font-medium text-gray-900 mt-0.5">{fmtMoney(view.missionsGiving)}</dd>
                              </div>
                            </dl>
                          </section>

                          {/* Giving detail */}
                          {(income || mission) && (
                            <section>
                              <h4 className="font-semibold text-gray-900 mb-3">Giving detail</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {income && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                      Budget receipts
                                    </h5>
                                    <dl className="space-y-1.5">
                                      {(
                                        [
                                          ['Congregational offering', income.congregationalOffering],
                                          ['Sponsoring church(es)', income.sponsoringChurches],
                                          ['Association(s)', income.associations],
                                          ['MBCB support', income.mbcb],
                                          ['Other support', income.otherSupport],
                                          ['Total', income.total],
                                        ] as [string, unknown][]
                                      ).map(([label, val]) => (
                                        <div key={label} className="flex justify-between gap-2">
                                          <dt className="text-gray-600">{label}</dt>
                                          <dd className="font-medium text-gray-900 tabular-nums">
                                            {fmtMoney(val)}
                                          </dd>
                                        </div>
                                      ))}
                                    </dl>
                                  </div>
                                )}
                                {mission && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                      Mission giving
                                    </h5>
                                    <dl className="space-y-1.5">
                                      {(
                                        [
                                          ['Cooperative Program', mission.cooperativeProgram],
                                          ['Association(s)', mission.associations],
                                          ['Margaret Lackey', mission.margaretLackey],
                                          ['Other mission causes', mission.otherMissionCauses],
                                          ['Total', mission.total],
                                        ] as [string, unknown][]
                                      ).map(([label, val]) => (
                                        <div key={label} className="flex justify-between gap-2">
                                          <dt className="text-gray-600">{label}</dt>
                                          <dd className="font-medium text-gray-900 tabular-nums">
                                            {fmtMoney(val)}
                                          </dd>
                                        </div>
                                      ))}
                                    </dl>
                                  </div>
                                )}
                              </div>
                            </section>
                          )}

                          {/* Worship by Sunday */}
                          {hasWeekly && (
                            <section>
                              <h4 className="font-semibold text-gray-900 mb-2">Worship by Sunday</h4>
                              <p className="text-xs text-gray-500 mb-3">
                                Sunday 1–5 are the Sundays of the reported month.
                              </p>
                              <div className="overflow-x-auto rounded-lg border border-gray-200">
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                      <th className="text-left font-semibold text-gray-700 p-2 pr-4">Metric</th>
                                      {SUNDAYS.map((sk, idx) => (
                                        <th key={sk} className="text-right font-semibold text-gray-700 p-2 w-14">
                                          Sun {idx + 1}
                                        </th>
                                      ))}
                                      <th className="text-right font-semibold text-gray-700 p-2 w-16">Sum</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const row = parseSundayRow(weekly, 'worshipAttendance');
                                      const sum = SUNDAYS.reduce((a, sk) => a + row[sk], 0);
                                      return (
                                        <tr className="border-b border-gray-100 last:border-0">
                                          <td className="p-2 pr-4 text-gray-800">Worship attendance</td>
                                          {SUNDAYS.map((sk) => (
                                            <td key={sk} className="p-2 text-right tabular-nums text-gray-900">
                                              {row[sk]}
                                            </td>
                                          ))}
                                          <td className="p-2 text-right font-medium tabular-nums text-gray-900">
                                            {sum}
                                          </td>
                                        </tr>
                                      );
                                    })()}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                          )}

                          {view.attendanceNotes && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Attendance note</h4>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {view.attendanceNotes}
                              </p>
                            </div>
                          )}

                          {view.givingNotes && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Giving note</h4>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{view.givingNotes}</p>
                            </div>
                          )}

                          {view.reviewNotes && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-yellow-900 mb-2">Review Notes</h4>
                              <p className="text-sm text-yellow-700 whitespace-pre-wrap">
                                {view.reviewNotes}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(selectedReport)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetailModal(false)}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {showApproveModal && selectedReport && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowApproveModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Approve Report</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Approve report from {selectedReport.planter?.name}?
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="Add any notes or feedback..."
                    />
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() =>
                      approveMutation.mutate({
                        id: selectedReport.id,
                        notes: reviewNotes || undefined,
                      })
                    }
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowApproveModal(false);
                      setReviewNotes('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Request Revision Modal */}
        {showRevisionModal && selectedReport && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowRevisionModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Request Revision
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Request revisions for report from {selectedReport.planter?.name}
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Revision Notes (Required)
                    </label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      required
                      rows={4}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="Explain what needs to be revised..."
                    />
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      if (reviewNotes.trim()) {
                        revisionMutation.mutate({
                          id: selectedReport.id,
                          notes: reviewNotes,
                        });
                      }
                    }}
                    disabled={!reviewNotes.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Request Revision
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRevisionModal(false);
                      setReviewNotes('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && selectedReport && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowDeleteModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Delete Report</h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete the {selectedReport.reportMonth}/{selectedReport.reportYear} report
                    from <strong>{selectedReport.planter?.name || 'this planter'}</strong>? This action cannot be undone.
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDeleteConfirm}
                    disabled={deleteMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleteMutation.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRemindUnsubmittedModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !remindUnsubmittedMutation.isPending && setShowRemindUnsubmittedModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Remind all with unsubmitted drafts
                  </h3>
                  <p className="text-sm text-gray-500">
                    This emails every funding-stage planter who has a draft or revision-needed report
                    about their most recent unsubmitted month. It does not create drafts, and it skips
                    post-funding planters and anyone whose reports are already submitted.
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => remindUnsubmittedMutation.mutate()}
                    disabled={remindUnsubmittedMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {remindUnsubmittedMutation.isPending ? 'Sending…' : 'Send reminders'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRemindUnsubmittedModal(false)}
                    disabled={remindUnsubmittedMutation.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showBulkDeleteModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !bulkDeleteMutation.isPending && setShowBulkDeleteModal(false)}
              ></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Delete selected reports</h3>
                  <p className="text-sm text-gray-500">
                    Permanently delete <strong>{selectedIds.size}</strong> selected report
                    {selectedIds.size === 1 ? '' : 's'}? This cannot be undone.
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                    disabled={bulkDeleteMutation.isPending || selectedIds.size === 0}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {bulkDeleteMutation.isPending ? 'Deleting…' : 'Delete all selected'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(false)}
                    disabled={bulkDeleteMutation.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MBCBReports;
